import { AfterViewInit, Directive, ElementRef, EventEmitter, NgZone, OnDestroy, Output } from '@angular/core';

/** Reports whether a visually clamped Astral label currently hides any text. */
@Directive({
  selector: '[astralLabelOverflow]',
  standalone: true,
})
export class AstralLabelOverflowDirective implements AfterViewInit, OnDestroy {
  @Output() astralLabelOverflowChange = new EventEmitter<boolean>();

  private mutationObserver: MutationObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private scheduled = false;
  private wasTruncated: boolean | null = null;
  private readonly viewportResizeListener = () => this.scheduleMeasurement();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasurement());
      this.resizeObserver.observe(this.elementRef.nativeElement);

      // Translation changes can replace text without changing the element's box.
      this.mutationObserver = new MutationObserver(() => this.scheduleMeasurement());
      this.mutationObserver.observe(this.elementRef.nativeElement, {
        characterData: true,
        childList: true,
        subtree: true,
      });

      window.addEventListener('resize', this.viewportResizeListener, { passive: true });
    });

    this.scheduleMeasurement();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    window.removeEventListener('resize', this.viewportResizeListener);
  }

  private scheduleMeasurement(): void {
    if (this.scheduled) {
      return;
    }

    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      const element = this.elementRef.nativeElement;
      const computedStyle = getComputedStyle(element);
      const configuredLineCount = Number.parseInt(computedStyle.getPropertyValue('--astral-field-label-lines'), 10);
      const visibleLineCount = Number.isFinite(configuredLineCount) && configuredLineCount > 0 ? configuredLineCount : 2;
      const triggerSpace = this.wasTruncated ? this.getOverflowTriggerSpace(element) : 0;
      const truncated =
        visibleLineCount === 1
          ? element.scrollWidth > element.clientWidth + triggerSpace + 0.5
          : this.measureNaturalText(element, computedStyle, triggerSpace).lineCount > visibleLineCount;

      if (truncated === this.wasTruncated) {
        return;
      }

      this.wasTruncated = truncated;
      this.zone.run(() => this.astralLabelOverflowChange.emit(truncated));
    });
  }

  private measureNaturalText(
    element: HTMLElement,
    computedStyle: CSSStyleDeclaration,
    triggerSpace: number,
  ): { inlineSize: number; lineCount: number } {
    const renderedWidth = element.getBoundingClientRect().width + triggerSpace;
    if (!renderedWidth) {
      return { inlineSize: 0, lineCount: 0 };
    }

    const clone = element.cloneNode(true) as HTMLElement;
    clone.removeAttribute('astralLabelOverflow');
    clone.style.setProperty('-webkit-line-clamp', 'unset');
    clone.style.setProperty('line-clamp', 'unset');
    clone.style.display = 'block';
    clone.style.height = 'auto';
    clone.style.fontFamily = computedStyle.fontFamily;
    clone.style.fontSize = computedStyle.fontSize;
    clone.style.fontStretch = computedStyle.fontStretch;
    clone.style.fontStyle = computedStyle.fontStyle;
    clone.style.fontVariant = computedStyle.fontVariant;
    clone.style.fontWeight = computedStyle.fontWeight;
    clone.style.inset = '0 auto auto 0';
    clone.style.letterSpacing = computedStyle.letterSpacing;
    clone.style.lineHeight = computedStyle.lineHeight;
    clone.style.overflow = 'visible';
    clone.style.overflowWrap = computedStyle.overflowWrap;
    clone.style.pointerEvents = 'none';
    clone.style.position = 'fixed';
    clone.style.visibility = 'hidden';
    clone.style.whiteSpace = computedStyle.whiteSpace;
    clone.style.wordBreak = computedStyle.wordBreak;
    clone.style.textTransform = computedStyle.textTransform;
    clone.style.width = `${renderedWidth}px`;
    clone.style.zIndex = '-1';
    document.body.appendChild(clone);

    const range = document.createRange();
    range.selectNodeContents(clone);
    const inlineSize = range.getBoundingClientRect().width;
    const lineTops: number[] = [];

    for (const rect of Array.from(range.getClientRects())) {
      if (!rect.width || !rect.height) {
        continue;
      }

      if (!lineTops.some(top => Math.abs(top - rect.top) < 1)) {
        lineTops.push(rect.top);
      }
    }

    range.detach();
    clone.remove();
    return { inlineSize, lineCount: lineTops.length };
  }

  private getOverflowTriggerSpace(element: HTMLElement): number {
    const labelContainer = element.closest(
      '.astral-field-shell__header-main, .astral-checkbox__layout, .astral-toggle__layout',
    );
    if (!labelContainer) {
      return 0;
    }

    const trigger = labelContainer.querySelector<HTMLElement>(
      '.astral-field-shell__label-overflow, .astral-checkbox__label-overflow, .astral-toggle__label-overflow',
    );
    if (!trigger) {
      return 0;
    }

    // Measure the live trigger and its real flex gap instead of duplicating CSS token math here.
    const gap = Number.parseFloat(getComputedStyle(labelContainer).columnGap) || 0;
    return trigger.getBoundingClientRect().width + gap;
  }
}
